import type { ComponentType } from 'react';
import type { ClassroomDto } from '../api/types.js';

/**
 * Registry of classroom-page modules.
 *
 * Each module declares:
 *   - `key`     stable identifier (matches SubPageKey when there's a
 *               live runtime page, or unique when there isn't)
 *   - `label`   human label for the configuration tab
 *   - `SettingsPanel`  React component rendered inside ClassroomDetail
 *                      when this tab is active. Receives the classroom
 *                      so the panel can fetch/persist settings via the
 *                      API client.
 *
 * The renderer (ClassroomDetail) only knows about this interface — it
 * does not import individual page modules. New pages plug in by
 * adding an entry below; nothing else has to change.
 *
 * A future "Pages Store" will move this from a static array to a
 * per-classroom enabled list persisted server-side. The shape stays.
 */
export interface ClassroomPageModule {
  key: string;
  label: string;
  SettingsPanel: ComponentType<{ classroom: ClassroomDto }>;
}

import { HomePageSettings } from './HomePage.module.js';
import { CalendarPageSettings } from './CalendarPage.module.js';
import { DayOfSchoolPageSettings } from './DayOfSchoolPage.module.js';
import { NumberPageSettings } from './NumberPage.module.js';
import { WeatherPageSettings } from './WeatherPage.module.js';
import { FeelingsPageSettings } from './FeelingsPage.module.js';
import { QuestionPageSettings } from './QuestionPage.module.js';
import { PledgesPageSettings } from './PledgesPage.module.js';
import { YouTubePageSettings } from './YouTubePage.module.js';
import { LinksPageSettings } from './LinksPage.module.js';

export const CLASSROOM_PAGE_MODULES: readonly ClassroomPageModule[] = [
  { key: 'home', label: 'Home', SettingsPanel: HomePageSettings },
  { key: 'calendar', label: 'Calendar', SettingsPanel: CalendarPageSettings },
  { key: 'day-of-school', label: 'Day of school', SettingsPanel: DayOfSchoolPageSettings },
  { key: 'number', label: 'Number', SettingsPanel: NumberPageSettings },
  { key: 'weather', label: 'Weather', SettingsPanel: WeatherPageSettings },
  { key: 'feelings', label: 'Feelings', SettingsPanel: FeelingsPageSettings },
  { key: 'question', label: 'Question', SettingsPanel: QuestionPageSettings },
  { key: 'pledges', label: 'Pledges', SettingsPanel: PledgesPageSettings },
  { key: 'youtube', label: 'YouTube', SettingsPanel: YouTubePageSettings },
  { key: 'links', label: 'Links', SettingsPanel: LinksPageSettings },
] as const;

export type ClassroomPageKey = (typeof CLASSROOM_PAGE_MODULES)[number]['key'];
